import { getRelationDivider, nbbCountries, secondaryRelationTypeOptions } from "../constants.ts";
import { PeriodicReviews } from "../types.ts";
import { getRelationDocRequirements } from "./getRelationDocRequirements.ts";
import { ParsedRelationAttributes } from "./parseRelationAttributes.ts";

export const addRelationFields = (options: {
  relationNumber: string;
  country: string;
  relationType: string;
  didTheRelationChangeId?: string;
  isExistingRelation?: boolean;
  isHighRisk?: boolean;
  relationResidenceCountry?: string;
  existingValues?: ParsedRelationAttributes;
}) => {
  const {
    relationNumber,
    country,
    relationType,
    didTheRelationChangeId,
    isExistingRelation,
    isHighRisk = false,
    relationResidenceCountry,
    existingValues,
  } = options;
  // We are doing it like this so that we filter out the unnecessary questions.
  // if it is a director and the country is in europe, then
  // only ask the defaults (name, nationality, dob, address)
  const isDirector = relationType === "director";
  const isInEurope = nbbCountries.includes(country);
  const askDefaultQuestionsOnly = isDirector && isInEurope;
  const countryToUse = askDefaultQuestionsOnly ? "Not in list" : country;

  const isEpbeDeBranch = country === "Germany";
  const isDeResidentRelation = relationResidenceCountry === "Germany";
  const requireDeTaxNumber = isEpbeDeBranch && isDeResidentRelation;

  const allFields = getAllFields(relationNumber, isHighRisk);

  if (requireDeTaxNumber) {
    const taxNumberFieldId = `relation_${relationNumber}_relation_type_updated_tax_number`;
    const taxNumberFieldIndex = allFields.findIndex((field) => field.id === taxNumberFieldId);
    const hasTaxNumberField = taxNumberFieldIndex !== -1;
    if (hasTaxNumberField) {
      allFields[taxNumberFieldIndex] = {
        ...allFields[taxNumberFieldIndex],
        onlyForCountries: [...(allFields[taxNumberFieldIndex].onlyForCountries ?? []), "Germany"],
        value: "PLEASE COMPLETE, INFORMATION MISSING",
      };
    }
  }

  // EPDU branch (United Arab Emirates) requires Document type, Document number,
  // Date of expiry and Country of issue for Signatory and UBO sections only —
  // auth contacts and directors are excluded per the FCTP country-specific matrix.
  const isEpduBranch = country === "United Arab Emirates";
  if (isEpduBranch) {
    const epduFieldSuffixes = ["doc_type", "doc_number", "expiry_date", "issue_country"];
    const epduRelationTypes = ["ubo", "signatory"];
    epduFieldSuffixes.forEach((suffix) => {
      const fieldId = `relation_${relationNumber}_relation_type_updated_${suffix}`;
      const fieldIndex = allFields.findIndex((field) => field.id === fieldId);
      const hasField = fieldIndex !== -1;
      if (hasField) {
        const field = allFields[fieldIndex];
        const relationTypeKey = `relation_${relationNumber}_relation_type_updated`;
        allFields[fieldIndex] = {
          ...field,
          onlyForCountries: [...(field.onlyForCountries ?? []), "United Arab Emirates"],
          renderConditions: {
            ...field.renderConditions,
            [relationTypeKey]: epduRelationTypes,
          },
        };
      }
    });
  }

  const isEpbeClBranch = country === "Chile";
  if (isEpbeClBranch) {
    const isClResidentRelation = relationResidenceCountry === "Chile";
    const docNumberFieldId = `relation_${relationNumber}_relation_type_updated_doc_number`;
    const docNumberFieldIndex = allFields.findIndex((field) => field.id === docNumberFieldId);
    const hasDocNumberField = docNumberFieldIndex !== -1;
    if (hasDocNumberField) {
      const docNumberAllowedRelationTypes = isClResidentRelation ? ["auth contact", "ubo", "director", "signatory"] : ["auth contact", "ubo", "signatory"];
      allFields[docNumberFieldIndex] = {
        ...allFields[docNumberFieldIndex],
        title: {
          en: "RUT Number",
          es: "Número de RUT",
          nl: "RUT-nummer",
          fr: "Numéro RUT",
          pt: "Número RUT",
          it: "Numero RUT",
          de: "RUT-Nummer",
          pl: "Numer RUT",
          ro: "Număr RUT",
          gr: "Αριθμός RUT",
        },
        onlyForCountries: [...(allFields[docNumberFieldIndex].onlyForCountries ?? []), "Chile"],
        renderConditions: {
          ...allFields[docNumberFieldIndex].renderConditions,
          [`relation_${relationNumber}_relation_type_updated`]: docNumberAllowedRelationTypes,
        },
      };
    }

    const addressNarrowing: { suffix: string; allowedTypes: string[] }[] = [
      { suffix: "city", allowedTypes: ["signatory", "ubo", "director"] },
      { suffix: "street_name", allowedTypes: ["ubo", "director"] },
      { suffix: "county_state_province", allowedTypes: ["ubo", "director"] },
      { suffix: "postal_code", allowedTypes: ["ubo", "director"] },
    ];
    for (const { suffix, allowedTypes } of addressNarrowing) {
      const fieldId = `relation_${relationNumber}_relation_type_updated_${suffix}`;
      const fieldIndex = allFields.findIndex((field) => field.id === fieldId);
      const hasField = fieldIndex !== -1;
      if (hasField) {
        allFields[fieldIndex] = {
          ...allFields[fieldIndex],
          renderConditions: {
            ...allFields[fieldIndex].renderConditions,
            [`relation_${relationNumber}_relation_type_updated`]: allowedTypes,
          },
        };
      }
    }
  }

  let filteredFieldsByCountry = allFields.filter(
    (field) => !field.onlyForCountries || field.onlyForCountries.includes(countryToUse),
  );

  if (isExistingRelation) {
    filteredFieldsByCountry = filteredFieldsByCountry.filter((field) => {
      const shouldInclude = !field.id.includes("dob") &&
        !field.id.includes("gender") &&
        !field.id.includes("birth_place") &&
        !field.id.includes("_info");
      return shouldInclude;
    });
  }

  const isEpbePlBranch = country === "Poland";
  const shouldRemovePlExpiredIdHardStop = isEpbePlBranch &&
    isExistingRelation === true &&
    didTheRelationChangeId !== undefined &&
    ["auth contact", "signatory", "ubo"].includes(relationType);

  if (shouldRemovePlExpiredIdHardStop) {
    const plAuthContactHardStopSuffixes = [
      "auth_contact_valid_certified_doc",
      "auth_contact_electronic_verification_method",
      "auth_contact_electronic_verification_method_message",
      "auth_contact_certified_doc",
    ];
    filteredFieldsByCountry = filteredFieldsByCountry.filter((field) => {
      const isHardStopField = plAuthContactHardStopSuffixes.some(
        (suffix) => field.id === `relation_${relationNumber}_relation_type_updated_${suffix}`,
      );
      return !isHardStopField;
    });
  }

  if (didTheRelationChangeId) {
    filteredFieldsByCountry = filteredFieldsByCountry.map((field) => {
      return {
        ...field,
        renderConditions: {
          ...field.renderConditions,
          [`${didTheRelationChangeId}`]: ["changed"],
        },
      };
    });
  }

  const hasExistingValues = existingValues !== undefined && Object.keys(existingValues).length > 0;
  if (hasExistingValues) {
    const fieldIdPrefix = `relation_${relationNumber}_relation_type_updated_`;
    filteredFieldsByCountry = filteredFieldsByCountry.map((field) => {
      const isPrefilledField = field.id.startsWith(fieldIdPrefix);
      if (!isPrefilledField) return field;
      const suffix = field.id.slice(fieldIdPrefix.length) as keyof ParsedRelationAttributes;
      const value = existingValues[suffix];
      const hasValue = value !== undefined && value.length > 0;
      if (!hasValue) return field;
      return { ...field, value };
    });
  }

  return filteredFieldsByCountry;
};

const getAllFields = (
  relationNumber: string,
  isHighRisk: boolean,
): PeriodicReviews.WebApp.ElementData[] => {
  const uboPercentage = isHighRisk ? "10" : "25";
  return [
    // Corporate Directors
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_legal_entity_name`,
      title: {
        en: "Legal Entity Name",
        es: "Nombre de entidad legal",
        nl: "Naam rechtspersoon",
        fr: "Nom de l'entité juridique",
        pt: "Nome legal da empresa",
        it: "Nome dell'entità legale",
        de: "Name der juristischen Person",
        pl: "Nazwa podmiotu prawnego",
        ro: "Persoană juridică",
        gr: "Όνομα νομικού προσώπου",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "corporate director",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_country_of_incorporation`,
      title: {
        en: "Country of incorporation",
        es: "País de incorporación",
        nl: "land van oprichting",
        fr: "Pays de constitution",
        pt: "País de registro",
        it: "Paese di costituzione",
        de: "Land der Unternehmensgründung",
        pl: "Kraj rejestracji",
        ro: "Țara de constituire",
        gr: "Χώρα σύστασης",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "corporate director",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`,
      title: {
        en: `Provide the director or corporate director information that manages the corporate director that has just been added`,
        es: `Proporcione información sobre el director o director corporativo que administre al director corporativo que acaba de agregar`,
        nl: `Geef informatie over de directeur of bedrijfsdirecteur die de zojuist toegevoegde bedrijfsdirecteur beheert`,
        fr: `Fournissez les informations du directeur corporatif gérant le directeur corporatif qui vient d'être ajouté`,
        pt: `Providencie informação sobre o representante/ diretor da empresa agora nomeada como diretora`,
        it: "Fornisci le informazioni del direttore o del direttore aziendale che gestisce il direttore aziendale appena aggiunto",
        de: `Geben Sie die Informationen des Geschäftsführers oder des Unternehmensleiters an, der den gerade hinzugefügten Unternehmensleiter verwaltet`,
        pl: `Uprzejmie prosimy o podanie informacji o dyrektorze (osoba fizyczna) lub dyrektorze korporacyjnym (osoba prawna), który zarządza nowo dodanym dyrektorem korporacyjnym`,
        ro: `Furnizați informații despre directorul sau directorul corporativ, care se referă la directorul corporativ care tocmai a fost adăugat`,
        gr: `Δώστε τα στοιχεία του διευθυντή ή του εταιρικού στελέχους που διευθύνει το διοικητικό συμβούλιο που μόλις προστέθηκε`,
        //TODO: add new language here
      },
      type: "select",
      options: secondaryRelationTypeOptions,
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "corporate director",
        ],
      },
    },
    // relation type info
    {
      id: `relation_${relationNumber}_relation_auth_contact_info`,
      title: {
        en: "Please provide details for the new authorised contact",
        es: "Proporcione detalles para el nuevo contacto autorizado",
        nl: "Gelieve de gegevens van de nieuwe geautoriseerde contactpersoon op te geven",
        fr: "Veuillez fournir les détails du nouveau contact autorisé",
        pt: `Providencie informações adicionais dos novos contactos autorizados`,
        it: "Fornisci i dettagli del nuovo contatto autorizzato",
        de: "Bitte geben Sie Details zum neuen autorisierten Kontakt an",
        pl: "Prosimy o podanie danych nowego kontaktu autoryzowanego",
        ro: "Vă rugăm să furnizați detalii pentru noul contact autorizat",
        gr: "Δώστε λεπτομέρειες για τη νέα εξουσιοδοτημένη επαφή",
        //TODO: add new language here
      },
      type: "paragraph",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: ["auth contact"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_ubo_info_25_percent`,
      title: {
        en: `Please state who the new UBO(s) of the company are and provide their personal details. In case there are intermediary layers in the shareholding structure holding ${uboPercentage}% or more of the shares please also include this information.`,
        es: `Por favor, proporcione quiénes son los nuevos titulares reales (UBOs) de la empresa y sus datos personales. En caso de que existan capas intermedias en la estructura accionarial que posean el ${uboPercentage}% o más de las acciones, incluya también esta información.`,
        nl: `Vermeld hierbij wie de nieuwe UBO('s) van de onderneming zijn en geef hun persoonsgegevens door. Indien er tussenliggende lagen in de aandeelhoudersstructuur zijn die ${uboPercentage}% of meer van de aandelen bezitten, gelieve deze informatie ook op te nemen.`,
        fr: `Veuillez indiquer qui sont les nouveaux UBO de l'entreprise et fournir leurs coordonnées. En cas de couches intermédiaires dans la structure actionnariale détenant ${uboPercentage}% ou plus des actions, veuillez également inclure cette information.`,
        pt: `Indique quem são os novos beneficiários efectivos (UBO) da empresa e os seus dados pessoais. Caso existam empresas intermédias na estrutura acionista com ${uboPercentage}% ou mais das ações, inclua também esta informação.`,
        it: `Si prega di indicare chi sono i nuovi Titolari Effettivi (UBO) dell'azienda e di fornire i loro dati personali. Nel caso ci siano livelli intermedi nella struttura azionaria che detengono il ${uboPercentage}% o più delle azioni, si prega di includere anche queste informazioni.`,
        de: `Bitte geben Sie an, wer die neuen wirtschaftlich Berechtigten (UBOs) des Unternehmens sind, und machen Sie deren persönliche Angaben. Falls es Zwischenebenen in der Beteiligungsstruktur gibt, die ${uboPercentage}% oder mehr der Anteile halten, fügen Sie bitte auch diese Informationen hinzu.`,
        pl: `Proszę wskazać, kto jest nowym beneficjentem rzeczywistym (UBO) firmy i podać jego dane osobowe. W przypadku istnienia warstw pośrednich w strukturze akcjonariatu posiadających ${uboPercentage}% lub więcej akcji, proszę również uwzględnić te informacje.`,
        ro: `Vă rugăm să precizați cine sunt noii beneficiari reali (UBO) ai companiei și să furnizați datele lor personale. În cazul în care există straturi intermediare în structura acționariatului care dețin ${uboPercentage}% sau mai mult din acțiuni, vă rugăm să includeți și aceste informații.`,
        gr: `Παρακαλώ δηλώστε ποιος είναι ο νέος πραγματικός δικαιούχος (UBO) της εταιρείας και δώστε τα προσωπικά του στοιχεία. Σε περίπτωση που υπάρχουν ενδιάμεσα επίπεδα στη μετοχική δομή που κατέχουν ${uboPercentage}% ή περισσότερο των μετοχών, παρακαλώ συμπεριλάβετε και αυτές τις πληροφορίες.`,
        //TODO: add new language here
      },
      type: "paragraph",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: ["ubo"],
      },
      onlyForCountries: [
        "United Kingdom",
        "Australia",
        "Canada",
        "Hong Kong",
        "United Arab Emirates",
        "Romania",
      ],
    },
    {
      id: `relation_${relationNumber}_relation_ubo_info_more_than_25_percent`,
      title: {
        en: `Please state who the new UBO(s) of the company are and provide their personal details. In case there are intermediary layers in the shareholding structure holding more than ${uboPercentage}% of the shares please also include this information.`,
        es: `Por favor, proporcione quiénes son los nuevos titulares reales (UBOs) de la empresa y sus datos personales. En caso de que existan capas intermedias en la estructura accionarial que posean más del ${uboPercentage}% de las acciones, incluya también esta información.`,
        nl: `Vermeld hierbij wie de nieuwe UBO('s) van de onderneming zijn en geef hun persoonsgegevens door. Indien er tussenliggende lagen in de aandeelhoudersstructuur zijn die meer dan ${uboPercentage}% van de aandelen bezitten, gelieve deze informatie ook op te nemen.`,
        fr: `Veuillez indiquer qui sont les nouveaux UBO de l'entreprise et fournir leurs coordonnées. En cas de couches intermédiaires dans la structure actionnariale détenant plus de ${uboPercentage}% des actions, veuillez également inclure cette information.`,
        pt: `Indique quem são os novos beneficiários efectivos (UBO) da empresa e os seus dados pessoais. Caso existam empresas intermédias na estrutura acionista com mais de ${uboPercentage}% das ações, inclua também esta informação.`,
        it: `Si prega di indicare chi sono i nuovi Titolari Effettivi (UBO) dell'azienda e di fornire i loro dati personali. Nel caso ci siano livelli intermedi nella struttura azionaria che detengono più del ${uboPercentage}% delle azioni, si prega di includere anche queste informazioni.`,
        de: `Bitte geben Sie an, wer die neuen wirtschaftlich Berechtigten (UBOs) des Unternehmens sind, und machen Sie deren persönliche Angaben. Falls es Zwischenebenen in der Beteiligungsstruktur gibt, die mehr als ${uboPercentage}% der Anteile halten, fügen Sie bitte auch diese Informationen hinzu.`,
        pl: `Proszę wskazać, kto jest nowym beneficjentem rzeczywistym (UBO) firmy i podać jego dane osobowe. W przypadku istnienia warstw pośrednich w strukturze akcjonariatu posiadających więcej niż ${uboPercentage}% akcji, proszę również uwzględnić te informacje.`,
        ro: `Vă rugăm să precizați cine sunt noii beneficiari reali (UBO) ai companiei și să furnizați datele lor personale. În cazul în care există straturi intermediare în structura acționariatului care dețin mai mult de ${uboPercentage}% din acțiuni, vă rugăm să includeți și aceste informații.`,
        gr: `Παρακαλώ δηλώστε ποιος είναι ο νέος πραγματικός δικαιούχος (UBO) της εταιρείας και δώστε τα προσωπικά του στοιχεία. Σε περίπτωση που υπάρχουν ενδιάμεσα επίπεδα στη μετοχική δομή που κατέχουν περισσότερο από ${uboPercentage}% των μετοχών, παρακαλώ συμπεριλάβετε και αυτές τις πληροφορίες.`,
        //TODO: add new language here
      },
      type: "paragraph",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: ["ubo"],
      },
      onlyForCountries: [
        "Belgium",
        "Czech Republic",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Spain",
        "Poland",
        "Netherlands",
        "Luxembourg",
        "Portugal",
        "Bulgaria",
        "Ireland",
        "Lithuania",
      ],
    },
    {
      id: `relation_${relationNumber}_relation_director_info`,
      title: {
        en: "Please state who the current director(s) of the company are.",
        es: "Por favor, confirme quiénes son los admisnitradores actuales de la empresa.",
        nl: "Gelieve te vermelden wie de huidige directeur(en) van de vennootschap zijn.",
        fr: "Veuillez indiquer qui sont les directeurs actuels de la société.",
        pt: `Indique quem são os atuais diretores da empresa.`,
        it: "Si prega di indicare chi sono gli attuali amministratori dell'azienda.",
        de: "Bitte geben Sie an, wer die aktuellen Geschäftsführer des Unternehmens sind.",
        pl: "Proszę podać, kto jest obecnym dyrektorem (dyrektorami) firmy.",
        ro: "Vă rugăm să precizați cine este (sunt) actualul (actualii) director(i) al (ai) companiei.",
        gr: "Παρακαλώ δηλώστε ποιος είναι ο τρέχων διευθυντής (ή διευθυντές) της εταιρείας.",
        //TODO: add new language here
      },
      type: "paragraph",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_signatory_info`,
      title: {
        en: "Please provide the updated details for the signatory.",
        es: "Por favor, proporcione los datos personales actualizados del firmante.",
        nl: "Gelieve de bijgewerkte gegevens van de ondertekenaar op te geven.",
        fr: "Veuillez fournir les détails mis à jour pour le signataire.",
        pt: `Providencie os dados atualizados do signatário.`,
        it: "Si prega di fornire i dettagli aggiornati per il firmatario.",
        de: "Bitte geben Sie die aktualisierten Daten für den Unterzeichner an.",
        pl: "Proszę podać zaktualizowane dane dla sygnatariusza.",
        ro: "Vă rugăm să furnizați detaliile actualizate pentru semnatar.",
        gr: "Παρακαλώ δώστε τα ενημερωμένα στοιχεία του υπογράφοντος.",
        //TODO: add new language here
      },
      type: "paragraph",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: ["signatory"],
      },
    },
    //relation data
    {
      id: `relation_${relationNumber}_relation_type_updated_first_name`,
      title: {
        en: `First Name`,
        es: `Nombre`,
        nl: `Naam`,
        fr: `Prénom`,
        pt: `Nome`,
        it: `Nome`,
        de: "Vorname",
        pl: "Imię",
        ro: "Prenume",
        gr: "Όνομα",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_middle_name`,
      title: {
        en: `Middle Name (optional)`,
        es: `Nombre compuesto (opcional)`,
        nl: `Midden-naam (optioneel)`,
        fr: `Deuxième prénom (optionnel)`,
        pt: `Nome do meio (opcional)`,
        it: `Nome del mezzo (opzionale)`,
        de: "Zweiter Vorname (optional)",
        pl: "Drugie imię (opcjonalnie)",
        ro: "Al doilea prenume (opțional)",
        gr: "Μεσαίο όνομα (προαιρετικό)",
        //TODO: add new language here
      },
      type: "text",
      required: false,
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_surname`,
      title: {
        en: `Surname`,
        es: `Apellido`,
        nl: `Achternaam`,
        fr: `Nom de famille`,
        pt: `Sobrenome`,
        it: `Cognome`,
        de: "Nachname",
        pl: "Nazwisko",
        ro: "Nume de familie",
        gr: "Επώνυμο",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_dob`,
      title: {
        en: `Date of Birth`,
        es: `Fecha de nacimiento`,
        nl: `Geboortedatum`,
        fr: `Date de naissance`,
        pt: `Data de nascimento`,
        it: `Data di nascita`,
        de: "Geburtsdatum",
        pl: "Data urodzenia",
        ro: "Data nașterii",
        gr: "Ημερομηνία γέννησης",
        //TODO: add new language here
      },
      type: "date",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_birth_place`,
      title: {
        en: `Place of Birth`,
        es: `Lugar de nacimiento`,
        nl: `Geboorteplaats`,
        fr: `Lieu de naissance`,
        pt: `Naturalidade`,
        it: `Luogo di nascita`,
        de: "Geburtsort",
        pl: "Miejsce urodzenia",
        ro: "Locul nașterii",
        gr: "Τόπος γέννησης",
        //TODO: add new language here
      },
      onlyForCountries: [
        "Belgium",
        "Bulgaria",
        "Czech Republic",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Netherlands",
        "Poland",
        "Portugal",
        "Romania",
        "Luxembourg",
      ],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_nationality`,
      title: {
        en: `Nationality`,
        es: `Nacionalidad`,
        nl: `Nationaliteit`,
        fr: `Nationalité`,
        pt: `Nacionalidade`,
        it: `Nazionalità`,
        de: "Staatsangehörigkeit",
        pl: "Narodowość",
        ro: "Naționalitate",
        gr: "Ιθαγένεια",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_gender`,
      title: {
        en: `Gender`,
        es: `Género`,
        nl: `Geslacht`,
        fr: `Genre`,
        pt: `Género`,
        it: `Sesso`,
        de: "Geschlecht",
        pl: "Płeć",
        ro: "Gen",
        gr: "Φύλο",
        //TODO: add new language here
      },
      onlyForCountries: [
        "Belgium",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Netherlands",
        "Poland",
        "Luxembourg",
      ],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_street_name`,
      title: {
        en: `Street name and number`,
        es: `Nombre y número de la calle`,
        nl: `Straatnaam en huisnummer`,
        fr: `Nom et numéro de rue`,
        pt: `Nome e número da rua`,
        it: `Nome e numero civico`,
        de: "Straße und Hausnummer",
        pl: "Nazwa ulicy i numer",
        ro: "Numele străzii și numărul",
        gr: "Όνομα οδού και αριθμός",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_city`,
      title: {
        en: `City`,
        es: `Ciudad`,
        nl: `Stad`,
        fr: `Ville`,
        pt: `Cidade`,
        it: `Città`,
        de: "Stadt",
        pl: "Miasto",
        ro: "Oraș",
        gr: "Πόλη",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_county_state_province`,
      title: {
        en: `County/State/Province`,
        es: `Condado/estado/provincia`,
        nl: `Provincie`,
        fr: `Département/État/Province`,
        pt: `Distrito`,
        it: `Contea/Stato/Provincia`,
        de: "Landkreis/Bundesland/Provinz",
        pl: "Hrabstwo/Stan/Prowincja",
        ro: "Județ/Stat/Provincie",
        gr: "Νομός/Πολιτεία/Επαρχία",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_country`,
      title: {
        en: `Country`,
        es: `País`,
        nl: `Land`,
        fr: `Pays`,
        pt: `País`,
        it: `Paese`,
        de: "Land",
        pl: "Kraj",
        ro: "Țară",
        gr: "Χώρα",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_postal_code`,
      title: {
        en: `Postal Code`,
        es: `Código Postal`,
        nl: `Postcode`,
        fr: `Code postal`,
        pt: `Código Postal`,
        it: `Codice Postale`,
        de: "Postleitzahl",
        pl: "Kod pocztowy",
        ro: "Cod poștal",
        gr: "Ταχυδρομικός κώδικας",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_phone_number`,
      title: {
        en: `Phone Number (landline or mobile)`,
        es: `Número de teléfono (teléfono fijo o móvil)`,
        nl: `Telefoonnummer (vaste of mobiel)`,
        fr: `Numéro de téléphone (ligne fixe ou mobile)`,
        pt: `Número de telefone`,
        it: `Numero di telefono (fisso o cellulare)`,
        de: "Telefonnummer (Festnetz oder Mobil)",
        pl: "Numer telefonu (stacjonarny lub komórkowy)",
        ro: "Număr de telefon (fix sau mobil)",
        gr: "Αριθμός τηλεφώνου (σταθερό ή κινητό)",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_email`,
      title: {
        en: "Email",
        es: "Email",
        nl: "Email",
        fr: "Adresse e-mail",
        pt: "Email",
        it: "Email",
        de: "E-Mail",
        pl: "E-mail",
        ro: "Email",
        gr: "Email",
        //TODO: add new language here
      },
      type: "email",
      required: true,
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_doc_type`,
      title: {
        en: `Document type`,
        es: `Tipo de Documento`,
        nl: `Type document`,
        fr: `Type de document`,
        pt: `Tipo de documento`,
        it: `Tipo di documento`,
        de: "Dokumententyp",
        pl: "Typ dokumentu",
        ro: "Tipul documentului",
        gr: "Τύπος εγγράφου",
        //TODO: add new language here
      },
      type: "text",
      onlyForCountries: [
        "Belgium",
        "Bulgaria",
        "Czech Republic",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Netherlands",
        "Poland",
        "Portugal",
        "Romania",
        "Spain",
        "Luxembourg",
      ],
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_doc_number`,
      title: {
        en: `Document Number`,
        es: `Número del Documento`,
        nl: `Document Nummer`,
        fr: `numéro de document`,
        pt: `Número de documento`,
        it: `Numero di documento`,
        de: "Dokumentennummer",
        pl: "Numer dokumentu",
        ro: "Numărul documentului",
        gr: "Αριθμός εγγράφου",
        //TODO: add new language here
      },
      type: "text",
      onlyForCountries: [
        "Belgium",
        "Bulgaria",
        "Czech Republic",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Poland",
        "Portugal",
        "Romania",
        "Spain",
        "Luxembourg",
        "Netherlands",
      ],
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_issue_date`,
      title: {
        en: `Date of Issue`,
        es: `Fecha de emisión`,
        nl: `Uitgavedatum`,
        fr: `Date d'émission`,
        pt: `Data de emissão`,
        it: `Data di emissione`,
        de: "Ausstellungsdatum",
        pl: "Data wydania",
        ro: "Data emiterii",
        gr: "Ημερομηνία έκδοσης",
        //TODO: add new language here
      },
      onlyForCountries: [
        "Belgium",
        "Bulgaria",
        "Czech Republic",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Netherlands",
        "Poland",
        "Portugal",
        "Luxembourg",
      ],
      type: "date",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_expiry_date`,
      title: {
        en: `Date of Expiry`,
        es: `Fecha de caducidad`,
        nl: `vervaldatum`,
        fr: `Date d'expiration`,
        pt: `Data de validade`,
        it: `Data di scadenza`,
        de: "Ablaufdatum",
        pl: "Data ważności",
        ro: "Data expirării",
        gr: "Ημερομηνία λήξης",
        //TODO: add new language here
      },
      type: "date",
      onlyForCountries: ["Netherlands", "Romania"],
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_issue_country`,
      title: {
        en: `Country of Issue`,
        es: `País de Emisión`,
        nl: `Land van afgifte`,
        fr: `Pays d'émission`,
        pt: `País de Emissão`,
        it: `Paese di emissione`,
        de: "Ausstellungsland",
        pl: "Kraj wydania",
        ro: "Țara emitentă",
        gr: "Χώρα έκδοσης",
        //TODO: add new language here
      },
      type: "text",
      onlyForCountries: [
        "Belgium",
        "Bulgaria",
        "Czech Republic",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Netherlands",
        "Poland",
        "Portugal",
        "Romania",
        "Spain",
        "Luxembourg",
      ],
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_issue_place`,
      title: {
        en: `Place of Issue`,
        es: `Lugar de emisión`,
        nl: `Plaats van afgifte`,
        fr: `Lieu d'émission`,
        pt: `Local de emissão`,
        it: `Luogo di emissione`,
        de: "Ausstellungsort",
        pl: "Miejsce wydania",
        ro: "Locul emiterii",
        gr: "Τόπος έκδοσης",
        //TODO: add new language here
      },
      onlyForCountries: ["France", "Greece", "Italy", "Netherlands"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_tax_number`,
      title: {
        en: `Tax Identification Number`,
        es: `Numero de identificación fiscal`,
        nl: `BTW-nummer`,
        fr: `Numéro d'identification fiscale`,
        pt: `Número de identificação fiscal`,
        it: `Codice Fiscale`,
        de: "Steueridentifikationsnummer",
        pl: "Numer identyfikacji podatkowej",
        ro: "Număr de identificare fiscală",
        gr: "Αριθμός φορολογικού μητρώου",
        //TODO: add new language here
      },
      onlyForCountries: [
        "Belgium",
        "Greece",
        "Italy",
        "Poland",
        "Portugal",
        "Luxembourg",
      ],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_occupation`,
      title: {
        en: `Occupation`,
        es: `Ocupación`,
        nl: `Beroep`,
        fr: `Profession`,
        pt: `Ocupação`,
        it: `Occupazione`,
        de: "Beruf",
        pl: "Zawód",
        ro: "Ocupație",
        gr: "Επάγγελμα",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_occupation_street_name`,
      title: {
        en: `Street name and number`,
        es: `Nombre y número de la calle`,
        nl: `Straatnaam en huisnummer`,
        fr: `Nom et numéro de rue`,
        pt: `Nome e número da rua`,
        it: `Nome e numero civico`,
        de: "Straße und Hausnummer",
        pl: "Nazwa ulicy i numer",
        ro: "Numele străzii și numărul",
        gr: "Όνομα οδού και αριθμός",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_occupation_city`,
      title: {
        en: `City`,
        es: `Ciudad`,
        nl: `Stad`,
        fr: `Ville`,
        pt: `Cidade`,
        it: `Città`,
        de: "Stadt",
        pl: "Miasto",
        ro: "Oraș",
        gr: "Πόλη",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_occupation_county_province_state`,
      title: {
        en: `County/State/Province`,
        es: `Condado/estado/provincia`,
        nl: `Provincie`,
        fr: `Département/État/Province`,
        pt: `Distrito`,
        it: `Contea/Stato/Provincia`,
        de: "Landkreis/Bundesland/Provinz",
        pl: "Hrabstwo/Stan/Prowincja",
        ro: "Județ/Stat/Provincie",
        gr: "Νομός/Πολιτεία/Επαρχία",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_occupation_country`,
      title: {
        en: `Country`,
        es: `País`,
        nl: `Land`,
        fr: `Pays`,
        pt: `País`,
        it: `Paese`,
        de: "Land",
        pl: "Kraj",
        ro: "Țară",
        gr: "Χώρα",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_occupation_postal_code`,
      title: {
        en: `Postal Code`,
        es: `Código Postal`,
        nl: `Postcode`,
        fr: `Code postal`,
        pt: `Código Postal`,
        it: `Codice Postale`,
        de: "Postleitzahl",
        pl: "Kod pocztowy",
        ro: "Cod poștal",
        gr: "Ταχυδρομικός κώδικας",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_fathers_name`,
      title: {
        en: `Father’s Name`,
        es: `Nombre del Padre`,
        nl: `Naam van de vader`,
        fr: `Le nom du père`,
        pt: `Nome do pai`,
        it: `Nome del padre`,
        de: "Name des Vaters",
        pl: "Imię ojca",
        ro: "Numele tatălui",
        gr: "Όνομα πατρός",
        //TODO: add new language here
      },
      type: "text",
      onlyForCountries: ["Greece"],
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated`]: [
          "auth contact",
          "ubo",
          "director",
          "signatory",
        ],
      },
    },

    //relation data for corporate director director
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_first_name`,
      title: {
        en: `First Name`,
        es: `Nombre`,
        nl: `Naam`,
        fr: `Prénom`,
        pt: `Nome`,
        it: `Nome`,
        de: "Vorname",
        pl: "Imię",
        ro: "Prenume",
        gr: "Όνομα",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_middle_name`,
      title: {
        en: `Middle Name (optional)`,
        es: `Nombre compuesto (opcional)`,
        nl: `Midden-naam (optioneel)`,
        fr: `Deuxième prénom (optionnel)`,
        pt: `Nome do meio (opcional)`,
        it: `Secondo nome (facoltativo)`,
        de: "Zweiter Vorname (optional)",
        pl: "Drugie imię (opcjonalnie)",
        ro: "Al doilea prenume (opțional)",
        gr: "Μεσαίο όνομα (προαιρετικό)",
        //TODO: add new language here
      },
      type: "text",
      required: false,
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_surname`,
      title: {
        en: `Surname`,
        es: `Apellido`,
        nl: `Achternaam`,
        fr: `Nom de famille`,
        pt: `Sobrenome`,
        it: `Cognome`,
        de: "Nachname",
        pl: "Nazwisko",
        ro: "Nume de familie",
        gr: "Επώνυμο",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_dob`,
      title: {
        en: `Date of Birth`,
        es: `Fecha de nacimiento`,
        nl: `Geboortedatum`,
        fr: `Date de naissance`,
        pt: `Data de nascimento`,
        it: `Data di nascita`,
        de: "Geburtsdatum",
        pl: "Data urodzenia",
        ro: "Data nașterii",
        gr: "Ημερομηνία γέννησης",
        //TODO: add new language here
      },
      type: "date",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_birth_place`,
      title: {
        en: `Place of Birth`,
        es: `Lugar de nacimiento`,
        nl: `Geboorteplaats`,
        fr: `Lieu de naissance`,
        pt: `Naturalidade`,
        it: `Luogo di nascita`,
        de: "Geburtsort",
        pl: "Miejsce urodzenia",
        ro: "Locul nașterii",
        gr: "Τόπος γέννησης",
        //TODO: add new language here
      },
      onlyForCountries: [
        "Belgium",
        "Bulgaria",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Netherlands",
        "Poland",
        "Portugal",
        "Romania",
      ],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_nationality`,
      title: {
        en: `Nationality`,
        es: `Nacionalidad`,
        nl: `Nationaliteit`,
        fr: `Nationalité`,
        pt: `Nacionalidade`,
        it: `Nazionalità`,
        de: "Staatsangehörigkeit",
        pl: "Narodowość",
        ro: "Naționalitate",
        gr: "Ιθαγένεια",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_corporate_director_director_updated_gender`,
      title: {
        en: `Gender`,
        es: `Género`,
        nl: `Geslacht`,
        fr: `Genre`,
        pt: `Género`,
        it: `Sesso`,
        de: "Geschlecht",
        pl: "Płeć",
        ro: "Gen",
        gr: "Φύλο",
        //TODO: add new language here
      },
      onlyForCountries: [
        "Belgium",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Netherlands",
        "Poland",
        "Luxembourg",
      ],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_street_name`,
      title: {
        en: `Street name and number`,
        es: `Nombre y número de la calle`,
        nl: `Straatnaam en huisnummer`,
        fr: `Nom et numéro de rue`,
        pt: `Nome e número da rua`,
        it: `Nome e numero civico`,
        de: "Straße und Hausnummer",
        pl: "Nazwa ulicy i numer",
        ro: "Numele străzii și numărul",
        gr: "Όνομα οδού και αριθμός",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_city`,
      title: {
        en: `City`,
        es: `Ciudad`,
        nl: `Stad`,
        fr: `Ville`,
        pt: `Cidade`,
        it: `Città`,
        de: "Stadt",
        pl: "Miasto",
        ro: "Oraș",
        gr: "Πόλη",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_county_state_province`,
      title: {
        en: `County/State/Province`,
        es: `Condado/estado/provincia`,
        nl: `Provincie`,
        fr: `Département/État/Province`,
        pt: `Distrito`,
        it: `Contea/Stato/Provincia`,
        de: "Landkreis/Bundesland/Provinz",
        pl: "Hrabstwo/Stan/Prowincja",
        ro: "Județ/Stat/Provincie",
        gr: "Νομός/Πολιτεία/Επαρχία",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_country`,
      title: {
        en: `Country`,
        es: `País`,
        nl: `Land`,
        fr: `Pays`,
        pt: `País`,
        it: `Paese`,
        de: "Land",
        pl: "Kraj",
        ro: "Țară",
        gr: "Χώρα",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_postal_code`,
      title: {
        en: `Postal Code`,
        es: `Código Postal`,
        nl: `Postcode`,
        fr: `Code postal`,
        pt: `Código Postal`,
        it: `Codice Postale`,
        de: "Postleitzahl",
        pl: "Kod pocztowy",
        ro: "Cod poștal",
        gr: "Ταχυδρομικός κώδικας",
        //TODO: add new language here
      },
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_phone_number`,
      title: {
        en: `Phone Number (landline or mobile)`,
        es: `Número de teléfono (teléfono fijo o móvil)`,
        nl: `Telefoonnummer (vaste of mobiel)`,
        fr: `Numéro de téléphone (ligne fixe ou mobile)`,
        pt: `Número de telefone`,
        it: `Numero di telefono (fisso o cellulare)`,
        de: "Telefonnummer (Festnetz oder Mobil)",
        pl: "Numer telefonu (stacjonarny lub komórkowy)",
        ro: "Număr de telefon (fix sau mobil)",
        gr: "Αριθμός τηλεφώνου (σταθερό ή κινητό)",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_doc_type`,
      title: {
        en: `Document type`,
        es: `Tipo de Documento`,
        nl: `Type document`,
        fr: `Type de document`,
        pt: `Tipo de documento`,
        it: `Tipo di documento`,
        de: "Dokumententyp",
        pl: "Typ dokumentu",
        ro: "Tipul documentului",
        gr: "Τύπος εγγράφου",
        //TODO: add new language here
      },
      type: "text",
      onlyForCountries: [
        "Belgium",
        "Bulgaria",
        "Czech Republic",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Netherlands",
        "Poland",
        "Portugal",
        "Romania",
        "Spain",
        "Luxembourg",
      ],
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_doc_number`,
      title: {
        en: `Document Number`,
        es: `Número del Documento`,
        nl: `Document Nummer`,
        fr: `numéro de document`,
        pt: `Número de documento`,
        it: `Numero di documento`,
        de: "Dokumentennummer",
        pl: "Numer dokumentu",
        ro: "Numărul documentului",
        gr: "Αριθμός εγγράφου",
        //TODO: add new language here
      },
      type: "text",
      onlyForCountries: [
        "Belgium",
        "Bulgaria",
        "Czech Republic",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Poland",
        "Portugal",
        "Romania",
        "Spain",
        "Luxembourg",
      ],
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_issue_date`,
      title: {
        en: `Date of Issue`,
        es: `Fecha de emisión`,
        nl: `Uitgavedatum`,
        fr: `Date d'émission`,
        pt: `Data de emissão`,
        it: `Data di emissione`,
        de: "Ausstellungsdatum",
        pl: "Data wydania",
        ro: "Data emiterii",
        gr: "Ημερομηνία έκδοσης",
        //TODO: add new language here
      },
      onlyForCountries: [
        "Belgium",
        "Bulgaria",
        "Czech Republic",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Netherlands",
        "Poland",
        "Portugal",
        "Romania",
        "Luxembourg",
      ],
      type: "date",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_expiry_date`,
      title: {
        en: `Date of Expiry`,
        es: `Fecha de caducidad`,
        nl: `vervaldatum`,
        fr: `Date d'expiration`,
        pt: `Data de validade`,
        it: `Data di scadenza`,
        de: "Ablaufdatum",
        pl: "Data ważności",
        ro: "Data expirării",
        gr: "Ημερομηνία λήξης",
        //TODO: add new language here
      },
      type: "date",
      onlyForCountries: ["Netherlands", "Portugal"],
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_issue_country`,
      title: {
        en: `Country of Issue`,
        es: `País de Emisión`,
        nl: `Land van afgifte`,
        fr: `Pays d'émission`,
        pt: `País emissor`,
        it: `Paese di emissione`,
        de: "Ausstellungsland",
        pl: "Kraj wydania",
        ro: "Țara emitentă",
        gr: "Χώρα έκδοσης",
        //TODO: add new language here
      },
      type: "text",
      onlyForCountries: [
        "Belgium",
        "Bulgaria",
        "Czech Republic",
        "France",
        "Germany",
        "Greece",
        "Italy",
        "Netherlands",
        "Poland",
        "Portugal",
        "Romania",
        "Spain",
        "Luxembourg",
      ],
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_issue_place`,
      title: {
        en: `Place of Issue`,
        es: `Lugar de emisión`,
        nl: `Plaats van afgifte`,
        fr: `Lieu d'émission`,
        pt: `Lugar de emissão`,
        it: `Luogo di emissione`,
        de: "Ausstellungsort",
        pl: "Miejsce wydania",
        ro: "Locul emiterii",
        gr: "Τόπος έκδοσης",
        //TODO: add new language here
      },
      onlyForCountries: ["France", "Greece", "Italy", "Netherlands"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_tax_number`,
      title: {
        en: `Tax Identification Number`,
        es: `Numero de identificación fiscal`,
        nl: `BTW-nummer`,
        fr: `Numéro d'identification fiscale`,
        pt: `Número de identificação fiscal`,
        it: `Numero di identificazione fiscale`,
        de: "Steueridentifikationsnummer",
        pl: "Numer identyfikacji podatkowej",
        ro: "Număr de identificare fiscală",
        gr: "Αριθμός φορολογικού μητρώου",
        //TODO: add new language here
      },
      onlyForCountries: [
        "Belgium",
        "Greece",
        "Italy",
        "Poland",
        "Portugal",
        "Luxembourg",
      ],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_occupation`,
      title: {
        en: `Occupation`,
        es: `Ocupación`,
        nl: `Beroep`,
        fr: `Profession`,
        pt: `Profissão`,
        it: `Occupazione`,
        de: "Beruf",
        pl: "Zawód",
        ro: "Ocupație",
        gr: "Επάγγελμα",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_occupation_street_name`,
      title: {
        en: `Street name and number`,
        es: `Nombre y número de la calle`,
        nl: `Straatnaam en huisnummer`,
        fr: `Nom et numéro de rue`,
        pt: `Nome e número da rua`,
        it: `Nome e numero civico`,
        de: "Straße und Hausnummer",
        pl: "Nazwa ulicy i numer",
        ro: "Numele străzii și numărul",
        gr: "Όνομα οδού και αριθμός",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_occupation_city`,
      title: {
        en: `City`,
        es: `Ciudad`,
        nl: `Stad`,
        fr: `Ville`,
        pt: `Cidade`,
        it: `Città`,
        de: "Stadt",
        pl: "Miasto",
        ro: "Oraș",
        gr: "Πόλη",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_occupation_county_province_state`,
      title: {
        en: `County/State/Province`,
        es: `Condado/estado/provincia`,
        nl: `Provincie`,
        fr: `Département/État/Province`,
        pt: `Distrito`,
        it: `Contea/Stato/Provincia`,
        de: "Landkreis/Bundesland/Provinz",
        pl: "Hrabstwo/Stan/Prowincja",
        ro: "Județ/Stat/Provincie",
        gr: "Νομός/Πολιτεία/Επαρχία",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_occupation_country`,
      title: {
        en: `Country`,
        es: `País`,
        nl: `Land`,
        fr: `Pays`,
        pt: `País`,
        it: `Paese`,
        de: "Land",
        pl: "Kraj",
        ro: "Țară",
        gr: "Χώρα",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    {
      id: `relation_${relationNumber}_relation_type_updated_corporate_director_director_occupation_postal_code`,
      title: {
        en: `Postal Code`,
        es: `Código Postal`,
        nl: `Postcode`,
        fr: `Code postal`,
        pt: `Código Postal`,
        it: `Codice Postale`,
        de: "Postleitzahl",
        pl: "Kod pocztowy",
        ro: "Cod poștal",
        gr: "Ταχυδρομικός κώδικας",
        //TODO: add new language here
      },
      onlyForCountries: ["Greece"],
      type: "text",
      renderConditions: {
        [`relation_${relationNumber}_relation_type_updated_corporate_director_secondary_type_update`]: ["director"],
      },
    },
    ...getRelationDocRequirements(relationNumber),
    getRelationDivider(relationNumber),
  ];
};
